import { Box, Card, Flex } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  type Node,
  type NodeChange,
  Position,
  ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useState } from 'react'
import DecodedMessageNode from '~/components/nodes/datahub/DecodedMessageNode'
import IncrementCountNode from '~/components/nodes/datahub/IncrementCountNode'
import InputMessageNode from '~/components/nodes/datahub/InputMessageNode'
import OutputMessageNode from '~/components/nodes/datahub/OutputMessageNode'
import SchemaValidationNode from '~/components/nodes/datahub/SchemaValidationNode'
import TransformationNode from '~/components/nodes/datahub/TransformationNode'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/dhsparkplugfanoutflow')({
  component: DHSparkplugFanOutFlowPage,
})

const schema = `
\`\`\`json
{}
`

const script = `
\`\`\`typescript
function transform(publish, context) {
  const topic = publish.topic;
  const metrics = publish.payload.metrics;

  metrics
    .forEach((metric) => {
      const payload = metric;
      const newTopic = topic + "/" + metric.name;

      context.branches["metrics"].addPublish({
        payload: payload,
        topic: newTopic,
      });
    });

  return publish;
}
`

const inputMessage = `
\`\`\`json
[Sparkplug B Protobuf encoded]
Inputs/Temperature: 29.2
Inputs/Humidity: 55.9
`

const decodedMessage = `
\`\`\`json
{
    "timestamp": 1486144502122,
    "metrics": [{
        "name": "Inputs/Temperature",
        "timestamp": 1486144502122,
        "dataType": "Float",
        "value": 29.2
    }, {
        "name": "Inputs/Humidity",
        "timestamp": 1486144502122,
        "dataType": "Float",
        "value": 55.9
    }],
    "seq": 0
}
`

const outputMessage1 = `
\`\`\`json
{
    "name": "Inputs/Temperature",
    "timestamp": 1486144502122,
    "dataType": "Float",
    "value": 29.2
}
`

const outputMessage2 = `
\`\`\`json
{
    "name": "Inputs/Humidity",
    "timestamp": 1486144502122,
    "dataType": "Float",
    "value": 55.9
}
`

const initNodes: Node[] = [
  // Input
  {
    id: '1',
    data: {
      label: 'InputMessage',
      topic: 'spBv1.0/DairyPlant/NDATA/Refrigeration',
      message: inputMessage,
    },
    type: 'inputMessageNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
    position: { x: 0, y: 0 },
  },
  {
    id: '2',
    data: {
      label: 'DecodedMessage',
      topic: 'spBv1.0/DairyPlant/NDATA/Refrigeration',
      message: decodedMessage,
    },
    type: 'decodedMessageNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
    position: { x: 500, y: 0 },
  },

  // DataHub
  {
    id: '3',
    data: { label: 'Schema Validation', schema: schema },
    type: 'schemaValidationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 1100, y: 150 },
  },
  {
    id: '4',
    data: {
      label: 'Log Error',
      logCounter: 'SchemaValidationError',
      message: 'Schema validation failed',
    },
    type: 'incrementCountNode',
    sourcePosition: Position.Top,
    targetPosition: Position.Top,
    position: { x: 1100, y: 600 },
  },
  {
    id: '5',
    data: { label: 'Transformation', script: script },
    type: 'transformationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 1600, y: 0 },
  },

  // Output
  {
    id: '6',
    data: {
      label: 'OutputMessage',
      topic: 'spBv1.0/DairyPlant/NDATA/Refrigeration/Inputs/Temperature',
      message: outputMessage1,
    },
    type: 'outputMessageNode',
    targetPosition: Position.Left,
    sourcePosition: Position.Left,
    position: { x: 2300, y: 0 },
  },
  {
    id: '7',
    data: {
      label: 'OutputMessage',
      topic: 'spBv1.0/DairyPlant/NDATA/Refrigeration/Inputs/Humidity',
      message: outputMessage2,
    },
    type: 'outputMessageNode',
    targetPosition: Position.Left,
    sourcePosition: Position.Left,
    position: { x: 2300, y: 350 },
  },
]

const initEdges: Edge[] = [
  {
    id: '12',
    source: '1',
    target: '2',
    label: 'Decode Protobuf',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '23',
    source: '2',
    target: '3',
    label: 'De-serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '34',
    source: '3',
    sourceHandle: 'b',
    target: '4',
    label: 'Error',
    style: { stroke: '#e91e63', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#e91e63' },
  },
  {
    id: '35',
    source: '3',
    sourceHandle: 'a',
    target: '5',
    label: 'Success',
    style: { stroke: '#10b981', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
  },
  {
    id: '56',
    source: '5',
    target: '6',
    label: 'Serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '57',
    source: '5',
    target: '7',
    label: 'Serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
]

const nodeTypes = {
  inputMessageNode: InputMessageNode,
  schemaValidationNode: SchemaValidationNode,
  incrementCountNode: IncrementCountNode,
  transformationNode: TransformationNode,
  outputMessageNode: OutputMessageNode,
  decodedMessageNode: DecodedMessageNode,
}

function DHSparkplugFanOutFlowPage() {
  const [nodes, setNodes] = useState<Node[]>(initNodes)
  const [edges, setEdges] = useState<Edge[]>(initEdges)

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Sparkplug B Fan-Out Flow"
        subtitle="HiveMQ DataHub pipeline for decoding Sparkplug B messages and fanning out individual metrics"
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            edges={edges}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            className="flow-customization"
          >
            <Background />
            <Controls />
          </ReactFlow>
        </Box>
      </Card.Root>
    </Flex>
  )
}
